import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { shippingApi, repairApi, authApi, reportsApi } from '../services/api';
import { ShippingRecord, RepairRecord, MonthlyReport } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import { formatVietnamCurrency } from '../utils/numberFormatter';
import DailyFinancialReport from '../components/DailyFinancialReport';
import MonthlyReportTable from '../components/MonthlyReportTable';
import TruckDetailedReportWidget from '../components/TruckDetailedReportWidget';
import DailyProfitChart from '../components/DailyProfitChart';
import VehicleTrackingMap from '../components/VehicleTrackingMap';

const DashboardPage: React.FC = () => {
  const { t } = useLanguage();
  const [shippingRecords, setShippingRecords] = useState<ShippingRecord[]>([]);
  const [repairRecords, setRepairRecords] = useState<RepairRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [monthlyReport, setMonthlyReport] = useState<MonthlyReport | null>(null);
  const user = authApi.getCurrentUser();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const now = new Date();
        const currentMonth = now.getMonth() + 1; // JS months are 0-based, API expects 1-based
        const currentYear = now.getFullYear();
        const [shippingResponse, repairResponse] = await Promise.all([
          shippingApi.getAll(currentYear, currentMonth),
          repairApi.getAll(currentYear, currentMonth)
        ]);
        setShippingRecords(shippingResponse.data);
        setRepairRecords(repairResponse.data);
        // Fetch monthly report for current month/year
        const reportResponse = await reportsApi.getMonthlyReport(currentYear, currentMonth);
        setMonthlyReport(reportResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="d-flex justify-content-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('loading')}</span>
          </div>
        </div>
      </div>
    );
  }

  // Use backend value if available
  // Filter out pending records - they don't have valid data
  const createdShippingRecords = shippingRecords.filter(record => record.status === 'created');
  
  const totalShippingRevenue = monthlyReport?.totals?.totalShippingRevenue ?? createdShippingRecords.reduce((sum, record) => sum + record.shippingCost, 0);
  const totalRepairCosts = monthlyReport?.totals?.totalRepairCosts ?? repairRecords.reduce((sum, record) => sum + record.cost, 0);
  const totalShippingProfit = monthlyReport?.totals?.totalShippingProfit ?? (totalShippingRevenue - totalRepairCosts);
  
  // Sort by most recent date and take top 5 (only created records)
  const recentShippingRecords = [...createdShippingRecords]
    .sort((a, b) => new Date(b.shippingDate).getTime() - new Date(a.shippingDate).getTime())
    .slice(0, 5);
  
  const recentRepairRecords = [...repairRecords]
    .sort((a, b) => new Date(b.repairDate).getTime() - new Date(a.repairDate).getTime())
    .slice(0, 5);

  // Helper function to truncate customer name
  const getTruncatedCustomerName = (name: string, maxLength: number = 20): string => {
    if (name.length <= maxLength) return name;
    return name.substring(0, maxLength) + '...';
  };

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-12">
          <h1>{t('dashboard')}</h1>
          <p className="text-muted">{t('welcome.back')}, {user?.firstName || user?.userName}!</p>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="row mb-4">
        <div className="col-md-3">
          <div className="card bg-primary text-white">
            <div className="card-body">
              <h5 className="card-title">{t('total.shipments')}</h5>
              <h2>{createdShippingRecords.length}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-success text-white">
            <div className="card-body">
              <h5 className="card-title">{t('shipping.revenue')}</h5>
              <h2>{formatVietnamCurrency(totalShippingRevenue)}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-danger text-white">
            <div className="card-body">
              <h5 className="card-title">{t('repair.costs')}</h5>
              <h2>{formatVietnamCurrency(totalRepairCosts)}</h2>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card bg-info text-white">
            <div className="card-body">
              <h5 className="card-title">{t('shipping.profit')}</h5>
              <h2>{formatVietnamCurrency(totalShippingProfit)}</h2>
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle Tracking Map */}
      <div className="row mb-4">
        <div className="col-12">
          <VehicleTrackingMap isWidget={true} />
        </div>
      </div>

      {/* Daily Profit Chart */}
      <div className="row mb-4">
        <div className="col-12">
          <DailyProfitChart />
        </div>
      </div>

      {/* Daily Financial Report */}
      <div className="row mb-4">
        <div className="col-12">
          <DailyFinancialReport />
        </div>
      </div>

      {/* Monthly Report */}
      <div className="row mb-4">
        <div className="col-12">
          <MonthlyReportTable />
        </div>
      </div>

      {/* Truck Detailed Report */}
      <div className="row mb-4">
        <div className="col-12">
          <TruckDetailedReportWidget />
        </div>
      </div>

      {/* Recent Records */}
      <div className="row">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t('recent.shipments')}</h5>
              <Link to="/shipping" className="btn btn-sm btn-primary">
                {t('view.all')}
              </Link>
            </div>
            <div className="card-body">
              {recentShippingRecords.length === 0 ? (
                <p className="text-muted">{t('no.shipping.records.found')}</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>{t('customer')}</th>
                        <th>{t('license.plate')}</th>
                        <th>{t('date')}</th>
                        <th>{t('shipping.revenue')}</th>
                      </tr>
                    </thead>
                    <tbody>
       {recentShippingRecords.map((record) => (
         <tr key={record.id}>
    <td title={record.customerName}>
       {getTruncatedCustomerName(record.customerName)}
         </td>
          <td>{record.licensePlate}</td>
           <td>{new Date(record.shippingDate).toLocaleDateString()}</td>
        <td>{formatVietnamCurrency(record.shippingCost)}</td>
       </tr>
            ))}
       </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="col-md-6">
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t('recent.repairs')}</h5>
              <Link to="/repair" className="btn btn-sm btn-primary">
                {t('view.all')}
              </Link>
            </div>
            <div className="card-body">
              {recentRepairRecords.length === 0 ? (
                <p className="text-muted">{t('no.repair.records.found')}</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm">
                    <thead>
                      <tr>
                        <th>{t('license.plate')}</th>
                        <th>{t('date')}</th>
                        <th>{t('cost')}</th>
                        <th>{t('shop')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentRepairRecords.map((record) => (
                        <tr key={record.id}>
                          <td>{record.licensePlate}</td>
                          <td>{new Date(record.repairDate).toLocaleDateString()}</td>
                          <td>{formatVietnamCurrency(record.cost)}</td>
                          <td>{record.repairShop || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;